import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Button, Card, Form, Input, Popconfirm, Select, Typography } from "antd";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { DateRangePicker as ZhDateRangePicker, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DATE_PICKER_FORMAT, DEFAULT_VALUE } from "@/config";
import { RECONCILIATION_STATE, RECEIPT_STATE, RECEIPT_TYPE } from "@/meta/purchase/receipt";
import { dateFormat, getRealyState } from "@/utils";
import { useUserStore } from "@/views/stores";

export interface ReceiptManagementRow {
    id?: number | string;
    projectName?: string;
    type?: number | string;
    supplierEnterprise?: { name?: string; realName?: string; mobile?: string };
    receiverEnterprise?: { name?: string; realName?: string; mobile?: string };
    quantity?: number | string;
    receiptConfirmedDate?: string;
    state?: number | string;
    reconciliationState?: number | string;
}

export interface ReceiptManagementQuery {
    page: number;
    size: number;
    keyword?: string;
    type?: number;
    enterprise?: string;
    beginTime?: string;
    endTime?: string;
    state?: number;
    reconciliationState?: number[];
    sortKey?: string;
    sortOrder?: string;
}

export interface ReceiptManagementPageResult {
    list?: ReceiptManagementRow[];
    total?: number;
}

export interface ReceiptManagementPageProps {
    loadReceipts?: (query: ReceiptManagementQuery) => Promise<ReceiptManagementPageResult>;
    closeReceipt?: (id: number | string) => Promise<unknown>;
}

type DateValue = string | { format?: (format: string) => string };

interface ReceiptManagementFilterValues {
    keyword?: string;
    type?: number | string;
    enterprise?: string;
    date?: [DateValue?, DateValue?];
    state?: number | string;
    reconciliationState?: Array<number | string> | number | string;
}

// 沿用旧 Vue useTable 的默认分页大小，避免收货列表首屏多取数据。
const defaultQuery: ReceiptManagementQuery = { page: 1, size: 10 };

/** 复用 Vue receiptService.paging 的收货分页接口和路径。 */
export async function loadReceiptManagement(query: ReceiptManagementQuery): Promise<ReceiptManagementPageResult> {
    const response = await request.service.get<ReceiptManagementPageResult>("/admin/purchase/goods/receipt/paging", {
        params: query
    });
    return response.data;
}

/** 将 Ant 日期值转换为 Vue dateFormat 发送给 receiptService 的日期字符串。 */
function formatFilterDate(value?: DateValue): string | undefined {
    if (!value) return undefined;
    if (typeof value === "string") return value;
    return value.format?.(DATE_PICKER_FORMAT);
}

/** 将筛选表单转换为旧 ProTable 的分页参数，空值不提交。 */
export function buildReceiptManagementQuery(values: ReceiptManagementFilterValues = {}): ReceiptManagementQuery {
    const query: ReceiptManagementQuery = { ...defaultQuery };
    const keyword = values.keyword?.trim();
    const enterprise = values.enterprise?.trim();

    if (keyword) query.keyword = keyword;
    if (enterprise) query.enterprise = enterprise;
    if (values.type !== undefined && values.type !== "") query.type = Number(values.type);
    if (values.state !== undefined && values.state !== "") query.state = Number(values.state);

    const beginTime = formatFilterDate(values.date?.[0]);
    const endTime = formatFilterDate(values.date?.[1]);
    if (beginTime && endTime) {
        query.beginTime = beginTime;
        query.endTime = endTime;
    }

    const reconciliationState = values.reconciliationState;
    if (Array.isArray(reconciliationState) && reconciliationState.length) {
        query.reconciliationState = reconciliationState.map(Number);
    } else if (reconciliationState !== undefined && reconciliationState !== "") {
        query.reconciliationState = [Number(reconciliationState)];
    }

    return query;
}

/** 显示 Vue 企业名称工具函数的同等优先级。 */
function enterpriseName(enterprise?: ReceiptManagementRow["supplierEnterprise"]): string {
    return enterprise?.name || enterprise?.realName || enterprise?.mobile || DEFAULT_VALUE;
}

/** 将签收状态值还原为 Vue getRealyState 后的枚举文案。 */
function receiptStateLabel(value?: number | string): string {
    const realState = getRealyState(Number(value));
    return RECEIPT_STATE.getIntroByCode(realState) || RECEIPT_STATE.getIntroByCode(RECEIPT_STATE.CONFIRMING.code);
}

/** 对账状态只在签收完成时展示，保持 Vue formatter 的占位符契约。 */
function reconciliationStateLabel(row: ReceiptManagementRow): string {
    if (getRealyState(Number(row.state)) !== RECEIPT_STATE.FINISHED.code) return DEFAULT_VALUE;
    return RECONCILIATION_STATE.getIntroByCode(Number(row.reconciliationState)) || DEFAULT_VALUE;
}

/** 采购收货列表：保留旧 Vue 的查询、排序、详情入口和关闭动作。 */
export function ReceiptManagementPage({
    loadReceipts: load = loadReceiptManagement,
    closeReceipt: close = closeReceiptManagement
}: ReceiptManagementPageProps) {
    const [filterForm] = Form.useForm();
    const [query, setQuery] = useState<ReceiptManagementQuery>(defaultQuery);
    const [rows, setRows] = useState<ReceiptManagementRow[]>([]);
    const [total, setTotal] = useState(0);
    const [reloadToken, setReloadToken] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const hasClosePermission = useUserStore(state => state.hasAnyPermission(["ROLE_PROJECT_MANAGE"]));
    const banButton = useUserStore(state => state.banButton);

    useEffect(() => {
        let disposed = false;
        void load(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("收货列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [load, query, reloadToken]);

    const handleClose = async (row: ReceiptManagementRow) => {
        if (row.id == null) return;
        try {
            await close(row.id);
            setReloadToken(value => value + 1);
        } catch {
            setErrorMessage("收货关闭失败");
        }
    };

    // 将 zhihao-ui-react 排序回调映射为旧 Vue receiptService 的字段和方向参数。
    const handleSortChange = ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
        setQuery(current => ({
            ...current,
            ...(prop === "receiptConfirmedDate"
                ? {
                      ...(order ? { sortKey: "receipt_confirmed_date" } : {}),
                      ...(order ? { sortOrder: order === "ascending" ? "asc" : "desc" } : {})
                  }
                : {})
        }));
    };

    // 用 zhihao-ui-react 列契约承接 Vue 收货列表的状态、日期排序和权限操作。
    const columns: TableColumnProps<ReceiptManagementRow>[] = [
        {
            key: "id",
            label: "编号",
            width: 145,
            prop: "id",
            render: ({ row }) =>
                row.id == null ? (
                    DEFAULT_VALUE
                ) : (
                    <Link to={`/purchasingManage/receiptManagement/detail/${row.id}`}>{String(row.id)}</Link>
                )
        },
        {
            key: "projectName",
            label: "项目",
            minWidth: 228,
            prop: "projectName",
            render: ({ row }) => row.projectName || DEFAULT_VALUE
        },
        {
            key: "type",
            label: "类型",
            minWidth: 165,
            prop: "type",
            render: ({ row }) => RECEIPT_TYPE.getIntroByCode(Number(row.type)) || DEFAULT_VALUE
        },
        {
            key: "supplierEnterprise",
            label: "供货单位",
            minWidth: 228,
            prop: "supplierEnterprise",
            render: ({ row }) => enterpriseName(row.supplierEnterprise)
        },
        {
            key: "receiverEnterprise",
            label: "签收单位",
            minWidth: 214,
            prop: "receiverEnterprise",
            render: ({ row }) => enterpriseName(row.receiverEnterprise)
        },
        {
            key: "quantity",
            label: "吨数",
            minWidth: 130,
            prop: "quantity",
            render: ({ row }) => String(row.quantity ?? DEFAULT_VALUE)
        },
        {
            key: "receiptConfirmedDate",
            label: "确认日期",
            minWidth: 150,
            prop: "receiptConfirmedDate",
            sortable: true,
            render: ({ row }) =>
                row.receiptConfirmedDate ? dateFormat(row.receiptConfirmedDate, DATE_PICKER_FORMAT) : DEFAULT_VALUE
        },
        {
            key: "state",
            label: "签收状态",
            width: 100,
            prop: "state",
            render: ({ row }) => receiptStateLabel(row.state)
        },
        {
            key: "reconciliationState",
            label: "对账状态",
            width: 100,
            prop: "reconciliationState",
            render: ({ row }) => reconciliationStateLabel(row)
        },
        {
            key: "operation",
            label: "操作",
            prop: "operation",
            render: ({ row }) => {
                const canClose =
                    hasClosePermission &&
                    !banButton &&
                    getRealyState(Number(row.state)) === RECEIPT_STATE.FINISHED.code &&
                    Number(row.reconciliationState) === RECONCILIATION_STATE.NOT_RECONCILIATION.code;
                const hasCloseAction =
                    hasClosePermission &&
                    getRealyState(Number(row.state)) === RECEIPT_STATE.FINISHED.code &&
                    Number(row.reconciliationState) === RECONCILIATION_STATE.NOT_RECONCILIATION.code;
                return hasCloseAction ? (
                    <Popconfirm
                        cancelText="取消"
                        disabled={!canClose}
                        okText="确定"
                        title="关闭后该签收信息将不再展示，确认关闭吗？"
                        onConfirm={() => void handleClose(row)}
                    >
                        <Button aria-label="关闭" disabled={!canClose} type="link">
                            关闭
                        </Button>
                    </Popconfirm>
                ) : null;
            }
        }
    ];

    return (
        <section aria-label="receipt-management-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<ReceiptManagementRow>
                    data={rows}
                    columns={normalizeOperationColumns(columns, rows)}
                    pagination={false}
                    rowKey={row => String(row.id)}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                收货管理
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="admin-table-filter-form mb-4"
                                form={filterForm}
                                layout="inline"
                                onFinish={values =>
                                    setQuery(buildReceiptManagementQuery(values as ReceiptManagementFilterValues))
                                }
                            >
                                <Form.Item label="项目" name="keyword">
                                    <Input
                                        aria-label="项目名称或编号"
                                        placeholder="请输入项目名称或编号搜索"
                                        maxLength={32}
                                    />
                                </Form.Item>
                                <Form.Item label="类型" name="type">
                                    <Select
                                        aria-label="收货类型"
                                        allowClear
                                        options={RECEIPT_TYPE.options}
                                        placeholder="请选择类型"
                                        style={{ minWidth: 150 }}
                                    />
                                </Form.Item>
                                <Form.Item label="供货单位" name="enterprise">
                                    <Input aria-label="供货单位" placeholder="请输入单位名" maxLength={32} />
                                </Form.Item>
                                <Form.Item label="确认日期" name="date">
                                    <ZhDateRangePicker
                                        aria-label="确认日期"
                                        endPlaceholder="确认结束时间"
                                        format={DATE_PICKER_FORMAT}
                                        rangeSeparator="-"
                                        startPlaceholder="确认起始时间"
                                        type="daterange"
                                        valueFormat={DATE_PICKER_FORMAT}
                                    />
                                </Form.Item>
                                <Form.Item label="签收状态" name="state">
                                    <Select
                                        aria-label="签收状态"
                                        allowClear
                                        options={RECEIPT_STATE.options}
                                        placeholder="请选择签收状态"
                                        style={{ minWidth: 150 }}
                                    />
                                </Form.Item>
                                <Form.Item label="对账状态" name="reconciliationState">
                                    <Select
                                        aria-label="对账状态"
                                        allowClear
                                        mode="multiple"
                                        options={RECONCILIATION_STATE.options}
                                        placeholder="请选择对账状态"
                                        style={{ minWidth: 180 }}
                                    />
                                </Form.Item>
                                <Button htmlType="submit" type="primary">
                                    查询
                                </Button>
                                <Button
                                    className="ml-2"
                                    onClick={() => {
                                        filterForm.resetFields();
                                        setQuery(defaultQuery);
                                    }}
                                >
                                    重置
                                </Button>
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                // 对齐旧版 Element Plus 分页的 sizes 选项，切换条数后回到第一页。
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                hideOnSinglePage
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

/** 复用 receiptService.closeById 的关闭接口。 */
export async function closeReceiptManagement(id: number | string): Promise<void> {
    await request.service.put(`/admin/purchase/goods/receipt/close/${id}`);
}

export default ReceiptManagementPage;
